import { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, Chip, ContentCard, Row } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import {
  showAmountStrikeThrough,
  DisplayStatus,
  getOriginalPaymentDirection,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import utils from "byzantine/src/utils";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import parseTemplateName from "../../common/templateNameUtils";
import styles from "../../PaymentActivity.module.scss";
import { PAYMENT_ACTIVITY_ROUTE } from "../../routes";
import type { PaymentMetadataProps } from "../PaymentActivityDrawer";
import ScheduleDetails from "./ScheduleDetails";

type TopCardProps = {
  paymentActivity: API.FormattedPaymentActivity;
  paymentMetadata: PaymentMetadataProps | null;
  bulkTitle: string;
  wireImportBatchTitle: string;
  hasDualApprovalPermission: boolean;
};

const TopCard = ({
  paymentActivity,
  paymentMetadata,
  bulkTitle,
  wireImportBatchTitle,
  hasDualApprovalPermission,
}: TopCardProps): ReactElement => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();

  const {
    displayAmountDollars,
    displayStatus,
    dualApprovalId,
    recipient,
    isCollectPayment,
    isInstantPayment,
    isSameDayPayment,
    isFedWirePayment,
    isInternationalWirePayment,
    isReversal,
    model,
  } = paymentActivity;

  const showStrikeThrough = showAmountStrikeThrough(displayStatus);
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;

  const handleClickViewApprovals = () => {
    navigate(
      `/approvals/pending?dualApprovalId=${dualApprovalId}`,
    );
  };

  const handleClickBulkTitle = () => {
    navigate(
      `/${PAYMENT_ACTIVITY_ROUTE}/bulk/${paymentActivity.bulkPaymentId}`,
    );
  };

  const handleClickWireImportBatch = () => {
    navigate(
      `/${PAYMENT_ACTIVITY_ROUTE}/file-import/${paymentActivity.wireImportBatchId}`,
    );
  };

  const getNameDescription = () => {
    const destinationName =
      paymentActivity?.destinationDetails?.beneficiary_name;

    const getDirection = () =>
      isCollectPayment
        ? l10n.getString("collect-label")
        : l10n.getString("send-label");

    const getConnectorWord = () =>
      (isCollectPayment
        ? l10n.getString("label-from")
        : l10n.getString("label-to")
      ).toLowerCase();

    const getName = () => destinationName || recipient?.name || "";

    // "Reverse collect from John" "Reverse send to John"
    if (isReversal) {
      return l10n.getString("approval-drawer-reversal-title", {
        transactionType:
          getOriginalPaymentDirection(paymentActivity) === "collect"
            ? "debit"
            : "credit",
        recipientName: getName(),
      });
    }

    // "Send instant payment to John" "Collect instant payment from John"
    if (isInstantPayment) {
      return `${getDirection()} ${l10n.getString(
        "instant-payment-label",
      )} ${getConnectorWord()} ${getName()}`;
    }

    //  "Send same-day ACH payment to John" "Collect same-day ACH payment to John"
    if (isSameDayPayment) {
      return `${getDirection()} ${l10n.getString(
        "same-day-ach-payment-label",
      )} ${getConnectorWord()} ${getName()}`;
    }

    // "Domestic wire to John" "International wire to John""
    if (isFedWirePayment || isInternationalWirePayment) {
      const paymentType = isFedWirePayment
        ? l10n.getString("fedwire-label")
        : l10n.getString("international-wire-label");
      return `${paymentType} ${getConnectorWord()} ${getName()}`;
    }

    // "Send ACH payment to John" "Collect ACH payment from John"
    return `${getDirection()} ${l10n.getString(
      "ach-payment-card-title",
    )} ${getConnectorWord()} ${getName()}`;
  };

  const quote = paymentMetadata?.quote;
  const hasFxQuote = !!(quote && quote.converted_amount);
  const parsedTemplateName = paymentMetadata?.template_name
    ? parseTemplateName(paymentMetadata.template_name)
    : null;

  const getFxDisplayValues = () => {
    if (!quote) return null;
    const isUsdSpecified = quote.specified_currency === "USD";

    const foreignCurrency = isUsdSpecified
      ? quote.converted_currency
      : quote.specified_currency;
    const foreignAmount = isUsdSpecified
      ? quote.converted_amount || "0"
      : String(quote.amount);
    const usdAmount = isUsdSpecified
      ? String(quote.amount)
      : quote.converted_amount || "0";
    const foreignCurrencyCode = foreignCurrency.substring(0, 2);

    return {
      headlinePrefix: isUsdSpecified ? "~" : "",
      headlineAmount: utils.currencyFormattedValue(
        foreignAmount,
        foreignCurrency,
      ),
      headlineCurrency: foreignCurrency,
      headlineFlag: countryCodeToFlag(foreignCurrencyCode),
      sendAmount: utils.currencyFormattedValue(usdAmount, "USD"),
      sendFlag: countryCodeToFlag("US"),
      sendLabel: isUsdSpecified
        ? l10n.getString("label-currency-amount-send-exactly")
        : l10n.getString("label-currency-amount-send-estimate"),
      exchangeRate: (() => {
        const parsedRate = parseFloat(quote.rate || "NaN");
        if (Number.isNaN(parsedRate)) return null;
        // Mimic useEffectiveConversionRate: invert when
        // converted_currency !== settlement_currency (USD)
        const effectiveRate =
          quote.converted_currency !== quote.settlement_currency
            ? 1 / parsedRate
            : parsedRate;
        return `1 USD = ${effectiveRate.toFixed(4)} ${foreignCurrency}`;
      })(),
    };
  };

  const fxDisplay = hasFxQuote ? getFxDisplayValues() : null;

  return (
    <div className="margin--top--l">
      <ContentCard kind="bordered">
        <h5 className="fontSize--m fontWeight--normal margin--bottom--xs">
          {getNameDescription()}
        </h5>
        {hasFxQuote && fxDisplay ? (
          <h1 className="fontColor--primary">
            <span
              className={cc({
                [styles.strikeThrough]: showStrikeThrough,
              })}
            >
              {fxDisplay.headlinePrefix}
              {fxDisplay.headlineAmount}{" "}
              <span className="fontSize--m">
                {fxDisplay.headlineFlag} {fxDisplay.headlineCurrency}
              </span>
            </span>
          </h1>
        ) : (
          <h1 className="fontColor--primary">
            <span
              className={cc({
                [styles.strikeThrough]: showStrikeThrough,
              })}
            >
              {displayAmountDollars}
            </span>
          </h1>
        )}
        {model === "SwiftWireTransfer" ? (
          <div className="fontSize--s margin--top--m">
            {l10n.getString("international-wire-fee-disclosure")}
          </div>
        ) : null}
        {hasFxQuote && fxDisplay && (
          <>
            <hr className="margin--bottom--l margin--top--l" />
            <Row>
              <Row.Item>
                <h5 className={styles.drawerLabelHeading}>
                  {fxDisplay.sendLabel}
                </h5>
              </Row.Item>
              <Row.Item shrink>
                {fxDisplay.sendAmount} {fxDisplay.sendFlag} USD
              </Row.Item>
            </Row>
            {fxDisplay.exchangeRate && (
              <Row className="margin--top--s">
                <Row.Item>
                  <h5 className={styles.drawerLabelHeading}>
                    {l10n.getString(
                      "international-wire-form-currency-estimated-exchange-rate",
                    )}
                  </h5>
                </Row.Item>
                <Row.Item shrink>{fxDisplay.exchangeRate}</Row.Item>
              </Row>
            )}
          </>
        )}
        <hr className="margin--bottom--l margin--top--l" />
        <ScheduleDetails paymentActivity={paymentActivity} />
        {parsedTemplateName ? (
          <div className="margin--top--m">
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("label-wire-template--template-name")}
            </h5>
            <div className={styles.templateNameRow}>
              <span className={styles.templateNameText}>
                {parsedTemplateName.displayName}
              </span>
              {parsedTemplateName.isDeleted && (
                <Chip
                  kind="warn"
                  label={l10n.getString("label-wire-template--deleted")}
                />
              )}
            </div>
          </div>
        ) : null}
        {isPendingApproval && dualApprovalId && hasDualApprovalPermission ? (
          <Button
            kind="plain"
            size="s"
            onClick={handleClickViewApprovals}
            testId="view-payment-approvals-button"
          >
            {l10n.getString("label-view-approvals")}
            <span className="narmi-icon-chevron-right" />
          </Button>
        ) : null}
        {bulkTitle || wireImportBatchTitle ? (
          <>
            <h5
              className={cc(
                "margin--top--m",
                "margin--none",
                styles.drawerLabelHeading,
              )}
            >
              {bulkTitle
                ? l10n.getString("label-associated-bulk")
                : l10n.getString("label-associated-file-payment")}
            </h5>
            <Button
              kind="plain"
              size="s"
              onClick={
                bulkTitle ? handleClickBulkTitle : handleClickWireImportBatch
              }
              testId="view-bulk-payment-button"
            >
              {l10n.getString("label-view")} {bulkTitle || wireImportBatchTitle}
              <span className="narmi-icon-chevron-right" />
            </Button>
          </>
        ) : null}
      </ContentCard>
    </div>
  );
};

export default TopCard;
